import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';
import jsQR from 'jsqr';
import { Button } from '../ui/Button.jsx';
import { Field } from '../ui/Field.jsx';
import { Sheet } from '../ui/Sheet.jsx';
import { useDb } from '../contexts.jsx';

const FACING_KEY = 'sps:facing';

export function Scan() {
  const { db } = useDb();
  const [, setLoc] = useLocation();
  const [facing, setFacing] = useState(() => localStorage.getItem(FACING_KEY) || 'environment');
  const [error, setError] = useState('');
  const [manual, setManual] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    let stream;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facing } },
          audio: false,
        });
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        loop();
      } catch (e) {
        setError('Camera unavailable — use "Type ID" below.');
      }
    })();
    return () => {
      cancelAnimationFrame(rafRef.current);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [facing]);

  function loop() {
    const v = videoRef.current,
      c = canvasRef.current;
    if (!v || v.readyState !== 4) {
      rafRef.current = requestAnimationFrame(loop);
      return;
    }
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(v, 0, 0);
    const img = ctx.getImageData(0, 0, c.width, c.height);
    let code;
    try {
      code = jsQR(img.data, img.width, img.height);
    } catch (e) {
      rafRef.current = requestAnimationFrame(loop);
      return;
    }
    if (code?.data) return onFound(code.data.trim());
    rafRef.current = requestAnimationFrame(loop);
  }

  async function onFound(memberId) {
    const person = await db.get('people', memberId);
    if (!person) {
      setError(`Unknown member ${memberId}`);
      rafRef.current = requestAnimationFrame(loop);
      return;
    }
    setLoc(`/family/${encodeURIComponent(person.family_key)}`);
  }
  function flip() {
    const next = facing === 'environment' ? 'user' : 'environment';
    localStorage.setItem(FACING_KEY, next);
    setFacing(next);
  }
  function submitManual(e) {
    e.preventDefault();
    if (manual.trim()) onFound(manual.trim());
  }

  return (
    <>
      <div
        style={{
          position: 'relative',
          aspectRatio: '3/4',
          background: '#000',
          borderRadius: 'var(--radius)',
          overflow: 'hidden',
        }}
      >
        <video
          ref={videoRef}
          playsInline
          muted
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            border: '3px dashed rgba(255,255,255,.6)',
            borderRadius: 'var(--radius)',
            margin: '15%',
            pointerEvents: 'none',
          }}
        />
        <Button style={{ position: 'absolute', top: 8, right: 8 }} onClick={flip}>
          Flip
        </Button>
      </div>
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      <Sheet>
        <form onSubmit={submitManual}>
          <Field label="Or type MemberId">
            <input value={manual} onInput={(e) => setManual(e.currentTarget.value)} />
          </Field>
          <Button variant="primary" block type="submit">
            Look up
          </Button>
        </form>
      </Sheet>
    </>
  );
}
